import { useCallback } from "react";
import { BlobApiResponse, useApiService } from "./apiService";

export type BugCategory = "Bug Report" | "Feature Request" | "Comment/General Feedback";
export type BugPriority = "Low" | "Medium" | "High" | "Critical";
export type BugStatus = "In Progress" | "Dev Done" | "Done" | "UAT";

export interface CreateBugLogRequest {
  title: string;
  category: BugCategory;
  descriptionStepToProduce: string;
  priority: BugPriority;
  status: BugStatus;
  assignedToUserId?: string;
  auditedByUserId?: string;
  attachmentUrl?: string;
}

export interface UpdateBugLogRequest extends CreateBugLogRequest {
  id: string;
}

export interface BugLogDto {
  id: string;
  title: string;
  category: BugCategory;
  descriptionStepToProduce: string;
  priority: BugPriority;
  status: BugStatus;
  tenantId: string;
  loggedByUserId: string;
  auditedByUserId?: string;
  assignedToUserId?: string;
  attachmentUrl?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface BugAttachmentMetadataDto {
  fileName: string;
  contentType: string;
}

export interface BugAttachmentDto {
  attachmentId: string;
  fileName: string;
  fileUrl: string;
  createdOn: string;
}

export const useBugLogService = () => {
  const apiService = useApiService();
  const baseEndpoint = "/api/app-management/bug-logs";

  const createBugLog = useCallback(
    async (request: CreateBugLogRequest): Promise<BugLogDto> => {
      const response = await apiService.apiCall<BugLogDto>({
        method: "POST",
        endpoint: baseEndpoint,
        payload: request,
        requireAuth: true,
      });
      return response.data;
    },
    [apiService]
  );

  const getBugLogs = useCallback(async (): Promise<BugLogDto[]> => {
    const response = await apiService.apiCall<BugLogDto[]>({
      method: "GET",
      endpoint: baseEndpoint,
      requireAuth: true,
    });
    return response.data ?? [];
  }, [apiService]);

  const updateBugLog = useCallback(
    async (request: UpdateBugLogRequest): Promise<BugLogDto> => {
      const { id, ...payload } = request;
      const response = await apiService.apiCall<BugLogDto>({
        method: "PUT",
        endpoint: `${baseEndpoint}/${id}`,
        payload,
        requireAuth: true,
      });
      return response.data;
    },
    [apiService]
  );

  const deleteBugLog = useCallback(
    async (id: string): Promise<boolean> => {
      const response = await apiService.apiCall<boolean>({
        method: "DELETE",
        endpoint: `${baseEndpoint}/${id}`,
        requireAuth: true,
      });
      return response.data === true;
    },
    [apiService]
  );

  const uploadBugLogAttachment = useCallback(
    async (id: string, file: File): Promise<BugLogDto> => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiService.apiCall<BugLogDto>({
        method: "POST",
        endpoint: `${baseEndpoint}/${id}/attachment`,
        payload: formData,
        requireAuth: true,
      });

      return response.data;
    },
    [apiService]
  );

  const uploadBugLogAttachments = useCallback(
    async (id: string, files: File[]): Promise<BugLogDto> => {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }

      const response = await apiService.apiCall<BugLogDto>({
        method: "POST",
        endpoint: `${baseEndpoint}/${id}/attachments`,
        payload: formData,
        requireAuth: true,
      });

      return response.data;
    },
    [apiService]
  );

  const getBugLogAttachmentBlob = useCallback(
    async (id: string): Promise<BlobApiResponse> => {
      return apiService.getBlob(`${baseEndpoint}/${id}/attachment`, true);
    },
    [apiService]
  );

  const getBugLogAttachmentMetadata = useCallback(
    async (id: string): Promise<BugAttachmentMetadataDto> => {
      const response = await apiService.apiCall<BugAttachmentMetadataDto>({
        method: "GET",
        endpoint: `${baseEndpoint}/${id}/attachment-metadata`,
        requireAuth: true,
      });

      return response.data;
    },
    [apiService]
  );

  const getBugLogAttachments = useCallback(
    async (id: string): Promise<BugAttachmentDto[]> => {
      const response = await apiService.apiCall<BugAttachmentDto[]>({
        method: "GET",
        endpoint: `${baseEndpoint}/${id}/attachments`,
        requireAuth: true,
      });

      return response.data ?? [];
    },
    [apiService]
  );

  const deleteBugLogAttachments = useCallback(
    async (id: string): Promise<void> => {
      await apiService.apiCall({
        method: "DELETE",
        endpoint: `${baseEndpoint}/${id}/attachment`,
        requireAuth: true,
      });
    },
    [apiService]
  );

  const deleteBugLogAttachment = useCallback(
    async (attachmentId: string): Promise<void> => {
      await apiService.apiCall({
        method: "DELETE",
        endpoint: `${baseEndpoint}/attachments/${attachmentId}`,
        requireAuth: true,
      });
    },
    [apiService]
  );

  return {
    createBugLog,
    getBugLogs,
    updateBugLog,
    deleteBugLog,
    uploadBugLogAttachment,
    uploadBugLogAttachments,
    getBugLogAttachments,
    getBugLogAttachmentBlob,
    getBugLogAttachmentMetadata,
    deleteBugLogAttachment,
    deleteBugLogAttachments,
  };
};
